import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  useMediaQuery,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExcelIcon from '@/assets/excel-icon.svg';
import debounce from 'lodash/debounce';
import { useNavigate, Link as RouterLink, Navigate } from 'react-router-dom';
import {
  useGetTransactionTimeDifferenceQuery,
  TransactionDetail,
  useGetAmcListQuery,
  useExportTransactionTimeDifferenceMutation,
} from '@/services/dashboardApi';
import { useGetAggregatorsQuery } from '@/services';
import { useSelector } from 'react-redux';
import { UserRole } from '@/types/user';
import { RootState } from '@/store/store';
import DateRangePicker from '@/components/DateRangePicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

// We compute this lazily so the range is fresh on each mount but stable within a render.
const getDefaultDateRange = () => ({
  start: startOfDay(subDays(new Date(), 7)),
  end: endOfDay(new Date()),
});

export default function Reports() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;

  const isAuthorized =
    role === UserRole.AMC_ADMIN || role === UserRole.ADMIN || role === UserRole.SUPPORT;
  if (!isAuthorized) {
    return <Navigate to="/dashboard" />;
  }

  const [startDate, setStartDate] = useState<Date | null>(() => getDefaultDateRange().start);
  const [endDate, setEndDate] = useState<Date | null>(() => getDefaultDateRange().end);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All');
  const [amcFilter, setAmcFilter] = useState('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('All');
  const [gatewayFilter, setGatewayFilter] = useState('All');
  const [acknowledgedByFilter, setAcknowledgedByFilter] = useState('All');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');

  // Debounce search to avoid flickering results
  const searchDebouncer = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 500),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      searchDebouncer.cancel();
    };
  }, [searchDebouncer]);

  // Fetch AMC data for filter options
  const { data: amcs = [] } = useGetAmcListQuery();

  // Reset to first page when any filter changes
  useEffect(() => {
    setPage(0);
  }, [
    statusFilter,
    amcFilter,
    transactionTypeFilter,
    gatewayFilter,
    acknowledgedByFilter,
    startDate,
    endDate,
    searchTerm,
  ]);

  const [exportReport, { isLoading: isExporting }] = useExportTransactionTimeDifferenceMutation();

  const handleExport = async () => {
    try {
      const blob = await exportReport(apiParams).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Transaction_Report_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Fetch dynamic aggregators based on selected AMC
  const { data: aggregatorsData } = useGetAggregatorsQuery(
    amcFilter && amcFilter !== 'all' ? amcFilter : undefined
  );

  // Build API parameters, only include non-empty filters
  const apiParams: any = {};

  // Add date range filter only if selected
  if (startDate) {
    apiParams.startDate = format(startDate, 'yyyy-MM-dd');
  }
  if (endDate) {
    apiParams.endDate = format(endDate, 'yyyy-MM-dd');
  }

  if (amcFilter && amcFilter !== 'all') {
    apiParams.amc = amcFilter;
  }
  if (transactionTypeFilter && transactionTypeFilter !== 'All') {
    apiParams.transactionType = transactionTypeFilter;
  }
  if (gatewayFilter && gatewayFilter !== 'All') {
    apiParams.paymentAggregator = gatewayFilter;
  }
  if (statusFilter && statusFilter !== 'All') {
    apiParams.status = statusFilter === 'initiated' ? 'INITIATED' : statusFilter.toLowerCase();
  }
  if (acknowledgedByFilter && acknowledgedByFilter !== 'All') {
    apiParams.acknowledgedBy = acknowledgedByFilter === 'null' ? null : acknowledgedByFilter;
  }

  // Add pagination and search
  apiParams.page = page + 1;
  apiParams.limit = rowsPerPage;
  if (searchTerm) {
    apiParams.search = searchTerm;
  }

  // Fetch transaction time difference data with API-supported filters
  const {
    data: transactionData,
    isLoading,
    error,
  } = useGetTransactionTimeDifferenceQuery(apiParams);
  // Process the new API response format
  const processTransactionData = () => {
    if (!transactionData) return [];
    return transactionData.allTransactions || [];
  };

  const allTransactions = processTransactionData();
  const StatusBadge = ({ status }: { status: string }) => {
    const colors = {
      success: { bg: theme.palette.success.light, color: theme.palette.success.main },
      settled: { bg: theme.palette.success.light, color: theme.palette.success.main },
      pending: { bg: theme.palette.warning.light, color: theme.palette.warning.main },
      failure: { bg: theme.palette.error.light, color: theme.palette.error.main },
      failed: { bg: theme.palette.error.light, color: theme.palette.error.main },
      initiated: { bg: theme.palette.warning.light, color: theme.palette.warning.main },
    };
    const normalized = status.toLowerCase();
    const style = colors[normalized as keyof typeof colors] || colors.pending;

    return (
      <Box
        sx={{
          backgroundColor: style.bg,
          color: style.color,
          borderRadius: '16px',
          padding: '0px 8px',
          display: 'inline-block',
          fontSize: '14px',
          fontWeight: theme.typography.h1.fontWeight,
          height: '30px',
          lineHeight: '28px',
          textAlign: 'center',
          minWidth: '80px',
        }}
      >
        {status ? status.toLocaleUpperCase() : 'N/A'}
      </Box>
    );
  };

  const handleTransactionClick = (transaction: TransactionDetail) => {
    // Navigate to payment detail page using the transaction ID
    navigate(`/payments/${transaction.id}`);
  };

  // Get all transactions from the API response
  const getAllTransactions = (): TransactionDetail[] => {
    return allTransactions;
  };

  // Apply client-side filters (only for filters not handled by API)
  const getFilteredTransactions = (): TransactionDetail[] => {
    const transactions = [...getAllTransactions()];
    // API now handles: amc, transactionType, paymentAggregator, status, date, acknowledgedBy, page, limit, search

    // Sort by initialized at (gatewayClicked) - still good to have client-side sort for the current page
    transactions.sort((a, b) => {
      const dateA = new Date(a.gatewayClicked).getTime();
      const dateB = new Date(b.gatewayClicked).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return transactions;
  };

  const filteredTransactions = getFilteredTransactions();
  // With server-side pagination, filteredTransactions already contains only the current page
  const paginatedTransactions = filteredTransactions;

  const totalCount = transactionData?.meta?.total || 0;

  // Get unique gateways from transactions (fallback if API doesn't return data)
  const uniqueGateways = Array.from(new Set(getAllTransactions().map((t) => t.gateway)));

  // Use dynamic aggregators if available, otherwise use unique gateways from data
  const gatewayOptions = aggregatorsData?.aggregators
    ? aggregatorsData.aggregators.map((agg) => agg.value)
    : uniqueGateways;

  // Filter options (matching PaymentList.tsx)
  const filterOptions = {
    status: ['settled', 'pending', 'failure', 'success', 'initiated'],
    transactionTypes: ['Lumpsum', 'Emandate', 'Hybrid'],
    amcs: amcs,
  };

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', width: '100%', px: isMobile ? 1 : 2 }}>
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
            color: theme.palette.text.secondary,
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Dashboard
        </Link>
        <Typography variant="subtitle1">
          Reports {totalCount > 0 ? `(${totalCount})` : ''}
        </Typography>
      </Breadcrumbs>

      {/* Filter and Control Container */}
      <Box
        sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '16px', p: 2, mb: 2 }}
      >
        {/* Filters Row */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Status Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {filterOptions.status.map((status) => (
                <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* AMC Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="AMC"
              value={amcFilter}
              onChange={(e) => setAmcFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {filterOptions.amcs.map((amc) => (
                <MenuItem key={amc.id} value={amc.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {amc.logoPath ? (
                      <img src={amc.logoPath} alt="" style={{ width: '20px', height: '20px' }} />
                    ) : (
                      <Avatar sx={{ width: 20, height: 20, fontSize: '12px' }}>
                        {amc.name.charAt(0)}
                      </Avatar>
                    )}
                    {amc.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Transaction Type Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Transaction Type"
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {filterOptions.transactionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: theme.palette.primary.main,
                        borderRadius: '50%',
                      }}
                    />
                    {type}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Payment Aggregator Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment Aggregator"
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {gatewayOptions.map((gateway) => (
                <MenuItem key={gateway} value={gateway}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, bgcolor: '#FF6B35', fontSize: '12px' }}>
                      {gateway.charAt(0)}
                    </Avatar>
                    {gateway}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Acknowledged By Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Acknowledged By"
              value={acknowledgedByFilter}
              onChange={(e) => setAcknowledgedByFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="queryApi">Query API</MenuItem>
              <MenuItem value="realTime">Real Time</MenuItem>
            </TextField>
          </Box>

          {/* Search Field */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 300px', minWidth: 200 }}>
            <TextField
              placeholder="Search Transaction / IH No"
              variant="outlined"
              size="small"
              fullWidth
              value={searchInputValue}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInputValue(value);
                searchDebouncer(value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInputValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        setSearchInputValue('');
                        searchDebouncer.cancel();
                        setSearchTerm('');
                      }}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* Date Range Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 300px', minWidth: 300 }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </Box>
        </Box>

        {/* Reset Filters Button and Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setStatusFilter('All');
                setAmcFilter('all');
                setTransactionTypeFilter('All');
                setGatewayFilter('All');
                setAcknowledgedByFilter('All');
                setSearchInputValue('');
                setSearchTerm('');
                searchDebouncer.cancel();
                const { start, end } = getDefaultDateRange();
                setStartDate(start);
                setEndDate(end);
              }}
              sx={{
                textTransform: 'none',
                alignSelf: isMobile ? 'flex-start' : 'center',
              }}
            >
              Reset Filters
            </Button>

            <Tooltip title="Export to Excel">
              <IconButton
                onClick={handleExport}
                disabled={isExporting}
                sx={{
                  p: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {isExporting ? (
                  <CircularProgress size={28} color="primary" />
                ) : (
                  <img src={ExcelIcon} alt="Export Excel" style={{ width: 28, height: 28 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min(page * rowsPerPage + 1, totalCount)} to{' '}
              {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount} entries
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value as string, 10));
                  setPage(0);
                }}
                displayEmpty
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>

            <Pagination
              count={Math.ceil(totalCount / rowsPerPage)}
              page={page + 1}
              onChange={(_event, value) => setPage(value - 1)}
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        </Box>
      </Box>

      {/* Data Container */}
      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '16px', p: 2 }}>
        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Error loading transaction data
            </Typography>
          </Box>
        )}

        {/* No Data Available State */}
        {!isLoading && transactionData && getAllTransactions().length === 0 && (
          <Box sx={{ width: '100%', p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}

        {/* Transaction Table */}
        {!isLoading && transactionData && getAllTransactions().length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>AMC / Asset</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Transaction Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Payment Aggregator
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Acknowledged By
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Initialized At
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Bank Ref No
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Gateway Message
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.length > 0 ? (
                  paginatedTransactions.map((transaction: TransactionDetail) => (
                    <TableRow
                      key={transaction.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleTransactionClick(transaction)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 'medium',
                            textDecoration: 'underline',
                            fontSize: '14px',
                          }}
                        >
                          {transaction.ihNoOrCart}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[300] }}>
                            {transaction.amcName?.charAt(0).toUpperCase() || 'N'}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>
                            {transaction.amcName || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: theme.palette.primary.main,
                              borderRadius: '50%',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>
                            {transaction.transactionType}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 'medium' }}>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 20, height: 20, bgcolor: '#FF6B35' }}>B</Avatar>
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>
                            {transaction.gateway}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {transaction.acknowledgedBy ? transaction.acknowledgedBy : 'None'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {new Date(transaction.gatewayClicked).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={transaction.swiftFlowErrorCode || 'No error code'} arrow>
                          <Typography variant="body2" sx={{ fontSize: '14px', cursor: 'help' }}>
                            {transaction.message || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {transaction.bank_ref_no || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {transaction.gatewayMessage || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={transaction.status} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>
                        No transactions found for the selected filters and date range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
